import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {CheckGlyph} from '@/components/ui/icons'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {SearchInput} from '@/components/ui/search-input'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {ListEmptyState} from '@/components/section-card'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {CertificacionTab} from '@/components/certificacion-tab'
import {KairosMark} from '@/components/kairos-mark'
import {NormaPropiaDialog} from '@/components/norma-propia-dialog'
import {RegistroRow} from '@/components/matriz-legal-registro-row'
import {MicroLabel} from '@/components/period-selector'
import {sgsstPageGuard} from '@/components/no-access-state'
import {PageNotices} from '@/components/page-notices'
import {PlanLockedPage} from '@/components/plan-upsell'
import {useBusiness, useCapabilities, useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useHighlightParam} from '@/hooks/common/use-highlight-param'
import {useTableState} from '@/hooks/common/use-table-state'
import {normLabel} from '@/lib/norma-label'
import {
  matrizLegalExportUrl,
  useDescartarNovedad,
  useIncorporarNovedad,
  useNovedadesLegal,
  useRegistroLegal,
  MATRIZ_LEGAL_PAGE_SIZE,
  type LegalItem,
  type Novedad,
  type PaginaNovedades,
} from '@/hooks/sgsst/matriz_legal/use-matriz-legal'
import {downloadBlob} from '@/lib/api'
import {translateApiError} from '@/lib/errors'

type TabKey = 'novedades' | 'registro' | 'certificacion'

/** Compartido por la rama con filas y la vacía: un grupo vacío conserva su encabezado y criterio (T4). */
function NovedadesEncabezado({conteo}: {conteo: number}) {
  const {t} = useTranslation()
  return (
    <div className="flex items-center gap-1.5">
      <h2 className="font-heading text-base text-foreground">
        {t('matrizLegal.tabs.novedadesConteo', {count: conteo})}
      </h2>
      <HelpIcon label={t('common.help.iconLabelFor', {campo: t('matrizLegal.tabs.novedades')})} testId="novedades-tip">
        {t('matrizLegal.novedades.tip')}
      </HelpIcon>
    </div>
  )
}

function NovedadesTab({
  businessId,
  pagina,
  page,
  onPage,
  readOnly,
}: {
  businessId: string
  pagina: PaginaNovedades
  page: number
  onPage: (page: number) => void
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const incorporar = useIncorporarNovedad(businessId)
  const [discarding, setDiscarding] = useState<Novedad | null>(null)
  const novedades = pagina.items
  const canIncorporar = pagina.can_incorporar
  const blockedReason = pagina.incorporar_blocked_reason
  const devOverride = pagina.incorporar_dev_override
  if (pagina.total === 0) {
    return (
      // Sin encabezado, una empresa sin novedades no tiene cómo enterarse de que Kairos vigila la normativa (T4).
      <section data-testid="novedades-empty">
        <NovedadesEncabezado conteo={0} />
        <div className="tone-green mt-3 flex items-start gap-3 rounded-xl px-4 py-3.5">
          {/* Insignia rellena y no `bg-accent`: el panel ya es `.tone-green` (var(--accent)) y la haría invisible. */}
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <CheckGlyph size={13} />
          </span>
          <p className="text-sm font-medium">{t('matrizLegal.novedades.alDiaTitle')}</p>
        </div>
      </section>
    )
  }
  // Solo dev: con `licencia_dev_override` el usuario de sistema puede actuar, pero se conserva la razón
  // «requiere licencia» porque `canIncorporar` sigue en false.
  const canAct = canIncorporar || devOverride
  const nuevas = novedades.filter((n) => n.kind === 'newly_applicable')
  const otras = novedades.filter((n) => n.kind !== 'newly_applicable')
  return (
    <section className="surface overflow-hidden" data-testid="matriz-legal-novedades">
      <div className="surface-divider border-b px-4 py-3">
        <NovedadesEncabezado conteo={pagina.total} />
        {!canIncorporar && blockedReason ? (
          <p className="mt-2 text-xs text-destructive" data-testid="matriz-legal-gate-reason">
            {t('matrizLegal.novedades.blocked', {reason: blockedReason})}
          </p>
        ) : null}
      </div>
      <ul className="px-4 pb-1.5">
        {nuevas.map((n) => (
          <li
            key={n.norm_ref}
            className="-mx-4 flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/30"
            data-testid="novedad-nueva">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground">
                <span className="font-mono text-xs text-muted-foreground">{normLabel(n)}</span> {n.titulo}
              </p>
              {n.basis_citations.length > 0 ? (
                // `ariaLabel` por norma: el rótulo se repite fila a fila (T11).
                <p className="mt-0.5">
                  <PorQue
                    label={t('matrizLegal.novedades.porque')}
                    ariaLabel={t('matrizLegal.novedades.porqueDe', {norma: normLabel(n)})}
                    testId="novedad-porque">
                    {n.basis_citations.join(' · ')}
                  </PorQue>
                </p>
              ) : null}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="min-h-11"
              disabled={!canAct || readOnly || incorporar.isPending}
              onClick={() => incorporar.mutate(n.norm_ref)}
              data-testid="incorporar-btn">
              {t('matrizLegal.novedades.incorporar')}
            </Button>
          </li>
        ))}
        {otras.map((n) => (
          <li
            key={n.norm_ref}
            className="-mx-4 flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/30"
            data-testid="novedad-otra">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                <span className="font-mono text-xs text-muted-foreground">{normLabel(n)}</span> {n.titulo}
                <Tag tone="neutral">{t(`matrizLegal.novedades.kind.${n.kind}`)}</Tag>
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="min-h-11"
              disabled={!canAct || readOnly}
              onClick={() => setDiscarding(n)}
              data-testid="descartar-btn">
              {t('matrizLegal.novedades.descartar')}
            </Button>
          </li>
        ))}
      </ul>
      <div className="px-4 pb-3">
        <PaginationBar page={page} pageSize={MATRIZ_LEGAL_PAGE_SIZE} total={pagina.total} onPageChange={onPage} />
      </div>
      {discarding ? (
        <DescartarDialog businessId={businessId} novedad={discarding} onClose={() => setDiscarding(null)} />
      ) : null}
    </section>
  )
}

function DescartarDialog({businessId, novedad, onClose}: {businessId: string; novedad: Novedad; onClose: () => void}) {
  const {t} = useTranslation()
  const descartar = useDescartarNovedad(businessId)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('matrizLegal.descartar.title', {norm: normLabel(novedad)})}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('matrizLegal.descartar.body')}</p>
        <textarea
          className="mt-2 min-h-20 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          placeholder={t('matrizLegal.descartar.placeholder')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          data-testid="descartar-motivo"
        />
        {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            disabled={!motivo.trim() || descartar.isPending}
            onClick={() =>
              descartar.mutate(
                {normRef: novedad.norm_ref, motivo: motivo.trim()},
                {onSuccess: onClose, onError: (e) => setError(translateApiError(e))}
              )
            }
            data-testid="descartar-confirm">
            {t('matrizLegal.descartar.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function parseTab(value: string | null): TabKey | null {
  return value === 'novedades' || value === 'registro' || value === 'certificacion' ? value : null
}

export function MatrizLegalPage() {
  const {t} = useTranslation()
  const {businessId} = useParams<{businessId: string}>()
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const capabilities = useCapabilities(businessId)
  const isSystemUser = useIsSystemUser()
  const readOnlyGodMode = useGodModeReadOnly()
  const [searchParams, setSearchParams] = useSearchParams()
  // `sortBy` no se manda: el backend no reordena y el registro sale en el orden de incorporación, el que
  // sigue el auditor.
  const tablaRegistro = useTableState('norm_ref')
  const tablaNovedades = useTableState('norm_ref')
  // Leído una vez al montar: `useHighlightParam` borra el parámetro apenas destella. Va al servidor para
  // que responda la página donde cae esa ficha, y se suelta al paginar o buscar.
  const [ancla, setAncla] = useState(() => searchParams.get('item'))
  const registro = useRegistroLegal(businessId, {
    page: tablaRegistro.page,
    search: tablaRegistro.search,
    extra: {ancla: ancla ?? undefined},
  })
  const novedades = useNovedadesLegal(businessId, {page: tablaNovedades.page, search: tablaNovedades.search})
  const [declarando, setDeclarando] = useState<{item: LegalItem | null} | null>(null)

  // Un `?item=` del plan abre siempre el Registro (D4); si no, Novedades cuando hay cola. Se lee una vez:
  // el destello lo borra de la URL y la pestaña saltaría a Novedades llevándose la ficha.
  const [llegaPorItem] = useState(() => searchParams.get('item') !== null)
  const tab =
    parseTab(searchParams.get('tab')) ??
    (llegaPorItem ? 'registro' : (novedades.data?.total ?? 0) > 0 ? 'novedades' : 'registro')
  // La señal espera las dos consultas: el efecto del destello no se reintenta y, corrido contra el
  // esqueleto, perdería el aterrizaje según cuál respuesta volviera antes.
  useHighlightParam('item', !!registro.data && !!novedades.data && tab === 'registro')

  const canAccess = !!capabilities?.sgsst_operar || isSystemUser
  const guard = sgsstPageGuard(
    businessLoading || registro.isLoading || novedades.isLoading,
    canAccess,
    registro.data && novedades.data
  )
  if (guard) return guard
  if (!businessId || !registro.data || !novedades.data) return null

  if (!capabilities.can_use_matriz_legal) {
    return (
      <PlanLockedPage feature={t('plans.feature.matrizLegal')} businessId={businessId} businessName={business?.name} />
    )
  }

  const readOnly = readOnlyGodMode
  // Declarar una norma propia es diseño, como incorporar: el mismo veredicto del backend gobierna ambos.
  const canDeclarar = novedades.data.can_incorporar || novedades.data.incorporar_dev_override
  const novedadesCount = novedades.data.total
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  const onExport = () => downloadBlob(matrizLegalExportUrl(businessId), 'matriz-legal.xlsx')

  return (
    <div className="space-y-4" data-testid="matriz-legal-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <KairosMark />
            <MicroLabel>{t('matrizLegal.eyebrow')}</MicroLabel>
          </div>
          <h1 className="mt-1 font-heading text-xl text-foreground">{t('matrizLegal.title')}</h1>
        </div>
        <Button size="sm" variant="outline" onClick={onExport} data-testid="matriz-legal-export">
          {t('matrizLegal.export')}
        </Button>
      </div>

      <PageNotices
        items={[
          registro.data.risk_class_missing && {
            id: 'risk-missing',
            severidad: 'neutral' as const,
            nodo: (
              <p
                className="surface flex flex-wrap items-center gap-2 px-4 py-3 text-xs text-muted-foreground"
                data-testid="risk-missing-banner">
                {t('matrizLegal.riskMissing')}
                <Link to={`/businesses/${businessId}/profile`} className="font-medium text-primary hover:underline">
                  {t('matrizLegal.completarPerfil')}
                </Link>
              </p>
            ),
          },
          registro.data.ciiu_missing && {
            id: 'ciiu-missing',
            severidad: 'neutral' as const,
            nodo: (
              <p
                className="surface flex flex-wrap items-center gap-2 px-4 py-3 text-xs text-muted-foreground"
                data-testid="ciiu-missing-banner">
                {t('matrizLegal.ciiuMissing')}
                <Link to={`/businesses/${businessId}/profile`} className="font-medium text-primary hover:underline">
                  {t('matrizLegal.completarPerfil')}
                </Link>
              </p>
            ),
          },
        ]}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="novedades" data-testid="tab-novedades">
            {novedadesCount > 0
              ? t('matrizLegal.tabs.novedadesConteo', {count: novedadesCount})
              : t('matrizLegal.tabs.novedades')}
          </TabsTrigger>
          <TabsTrigger value="registro" data-testid="tab-registro">
            {t('matrizLegal.tabs.registro', {count: registro.data.total})}
          </TabsTrigger>
          <TabsTrigger value="certificacion" data-testid="tab-certificacion">
            {t('matrizLegal.tabs.certificacion')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="novedades" className="pt-4">
          <NovedadesTab
            businessId={businessId}
            pagina={novedades.data}
            page={tablaNovedades.page}
            onPage={tablaNovedades.setPage}
            readOnly={readOnly}
          />
        </TabsContent>

        <TabsContent value="registro" className="pt-4">
          <section className="surface overflow-hidden">
            <div className="surface-divider flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-heading text-base text-foreground">
                  {t('matrizLegal.tabs.registro', {count: registro.data.total})}
                </h2>
                <SearchInput
                  value={tablaRegistro.searchInput}
                  onChange={(valor) => {
                    setAncla(null)
                    tablaRegistro.setSearchInput(valor)
                  }}
                  placeholder={t('matrizLegal.registro.buscar')}
                  data-testid="matriz-legal-buscar"
                />
              </div>
              {/* Secundario a propósito: declarar a mano es la salida para lo que Kairos no puede saber, no la entrada. */}
              <Button
                size="sm"
                variant="outline"
                className="min-h-11"
                disabled={readOnly || !canDeclarar}
                onClick={() => setDeclarando({item: null})}
                data-testid="declarar-propia-btn">
                {t('matrizLegal.propia.cta.label')}
              </Button>
            </div>
            {registro.data.total === 0 ? (
              <div data-testid="registro-empty">
                <ListEmptyState
                  page
                  title={t(tablaRegistro.search ? 'matrizLegal.registro.vacioBusqueda' : 'matrizLegal.registro.empty', {
                    termino: tablaRegistro.search,
                  })}
                  action={
                    tablaRegistro.search ? (
                      <Button size="sm" variant="outline" onClick={() => tablaRegistro.setSearchInput('')}>
                        {t('matrizLegal.registro.limpiarBusqueda')}
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => setTab('novedades')}>
                        {t('matrizLegal.registro.emptyCta')}
                      </Button>
                    )
                  }
                />
              </div>
            ) : (
              <>
                {/* `RegistroRow` es un `<li>` y no una fila de `DataTable`: esta rinde el detalle en un `<tr>`
                    hermano, fuera del testid de la fila, y partiría el subárbol que recorren los e2e. */}
                <ul className="px-4 pb-1.5">
                  {registro.data.items.map((item) => (
                    <RegistroRow
                      key={item.id}
                      businessId={businessId}
                      item={item}
                      readOnly={readOnly}
                      onEditarPropia={(propia) => setDeclarando({item: propia})}
                    />
                  ))}
                </ul>
                <div className="px-4 pb-3">
                  {/* La página del pie es la que contestó el servidor: con un ancla puede no ser la del estado local. */}
                  <PaginationBar
                    page={registro.data.page}
                    pageSize={MATRIZ_LEGAL_PAGE_SIZE}
                    total={registro.data.total}
                    onPageChange={(pagina) => {
                      setAncla(null)
                      tablaRegistro.setPage(pagina)
                    }}
                  />
                </div>
              </>
            )}
          </section>
        </TabsContent>

        <TabsContent value="certificacion" className="pt-4">
          <CertificacionTab businessId={businessId} artefacto="matriz_legal" />
        </TabsContent>
      </Tabs>

      {declarando ? (
        <NormaPropiaDialog
          businessId={businessId}
          item={declarando.item}
          onClose={() => setDeclarando(null)}
          onIrANovedades={() => setTab('novedades')}
        />
      ) : null}
    </div>
  )
}
